import { Component, HostBinding, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgForm } from '@angular/forms';
import { CellClickedEvent, RowNode, CellClassParams } from 'ag-grid-community';
import * as _ from 'underscore';
import { isBoolean, isUndefined, isNullOrUndefined } from '../../../../models/utils';
import { DrRequestInfo } from '../../../../models/dr-request-info';
import { Endpoint, HelpIndex } from '../../../../models/enums';
import { AlignDirection, CellActionType, CellTemplateType, GenericColDef, GridContext } from '../../../../models/grid-context';
import { ActionType, ObjectActionListType, ObjectActionSet, Permissions } from '../../../../models/permission';
import { Role, RolePermissionTabType, RolePermissionViewData, SecureObjectGroupType, SecureObjectPermissionLevel, PermissionDetail } from '../../../../models/role';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { AutoProjectCreation } from '../../../../models/preferences';


@Component({
    standalone: false,
    selector: 'res-role-permissions',
    templateUrl: './role-permissions.component.html',
    styleUrls: ['./role-permissions.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class RolePermissionsComponent implements OnInit {
    readonly RolePermissionTabType = RolePermissionTabType;
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild('panelGrid', { static: true }) panelGrid: GenericGridComponent;
    @ViewChild('navigationTreeNodeGrid', { static: true }) navigationTreeNodeGrid: GenericGridComponent;
    @ViewChild('settingGrid', { static: true }) settingGrid: GenericGridComponent;
    @ViewChild('tlsObjectGrid', { static: true }) tlsObjectGrid: GenericGridComponent;
    @ViewChild('eDiscoveryForm', { static: true }) eDiscoveryForm: NgForm;

    tabType: RolePermissionTabType;

    // member variables
    name: 'Role Permissions';
    automationId: 'org-settings-tree-general-roles';
    icon: 'fa-user-md';
    iconColor: 'color-yellow-07';
    childPadding: 5;
    handle: '';
    templateName: string;
    isBusy = false;
    isBusyMessage = 'Loading ...';
    validChanges = true;

    helpIndex: HelpIndex;
    gridContext: GridContext;
    hasPermissions: boolean;

    // variables - across tabs
    isReadOnly: boolean;
    selectedRole: Role;
    viewData: RolePermissionViewData;
    roleOriginalObjectActionList: Map<string, ObjectActionSet>;

    // Setup panels  

    // (Common) Other checkboxes
    connectorAccessCheckbox: boolean;
    projectMonitoringAccessCheckbox: boolean;

    // panel grid
    panelGridName = 'Panel';
    panelGridColDefs: any;
    panelAutomationId = 'panel-grid-automation-id';

    // navigation tree node grid
    navigationTreeNodeGridName = 'Navigation Tree Node';
    navigationTreeColDefs: any;
    navigationTreeNodeAutomationId = 'navigation-tree-node-automation-id';

    // setting grid
    settingGridName = 'Setting';
    settingGridColDefs: any;
    settingAutomationId = 'setting-grid-automation-id';

    // Document Actions checkboxes
    addRemoveTagsCheckbox: boolean;
    addRemoveFolderCheckbox: boolean;
    addRemoveCustodianCheckbox: boolean;
    downloadNativeCheckbox: boolean;
    downloadPDFCheckbox: boolean;

    // Other checkboxes
    auditAccessCheckbox: boolean;
    documentReportsCheckbox: boolean;
    workflowLockUnlockCheckbox: boolean;

    // tls object grid
    tlsObjectGridName = 'TLS Object Grid';
    tlsObjectGridColDefs: any;
    tlsObjectAutomationId = 'tls-object-grid-automation-id';

    // Reef Express Other checkboxes
    openInRelativityCheckbox: boolean;
    billingOverviewCheckbox: boolean;
    drAccessCheckbox: boolean;
    viewOthersDataSetsCheckbox: boolean;
    isTLSExpress: boolean;
    exportToRelativityCheckbox: boolean;

    dirtyCount = 0;
    openInTitle: string;
    exportToTitle: string;

    // Methods
    get isDirty(): boolean {
        if (this.eDiscoveryForm) {
            return this.eDiscoveryForm.dirty;
        }
        return false;
    }

    set isDirty(value: boolean) {
        if (this.eDiscoveryForm && this.eDiscoveryForm.control) {
            if (value) {
                this.eDiscoveryForm.control.markAsDirty();
            } else {
                this.eDiscoveryForm.control.markAsPristine();
            }
        }
    }

    // class methods
    requestInfo: DrRequestInfo;

    constructor(private commonService: CommonService, private restService: RestService) {
    }

    openHelp = () => this.commonService.openHelp(this.helpIndex);

    ngOnInit() {
        this.hasPermissions = Permissions.checkUserPermission(ObjectActionListType.ROLE, ActionType.CREATE);
        this.helpIndex = HelpIndex.org_roles_permissions_dlg;
        this.isReadOnly = false;
    }

    loadData(): void {
        this.isBusyMessage = 'Loading Role Permissions ...';
        this.isBusy = true;
        this.isDirty = false;
        if (this.viewData == null) {
            this.restService.post(Endpoint.permissionManager_getSecureObjectGroups, {}).then(responseAllFields => {
                if (responseAllFields && responseAllFields.status === 'SUCCESS' && responseAllFields.groupedSecureObjectTypes) {
                    // initialize all the default role view data related before applying role specific settings
                    this.viewData = new RolePermissionViewData(responseAllFields.groupedSecureObjectTypes);
                    if (this.selectedRole && this.selectedRole.handle) {
                        this.applyRole();
                    }
                }
            }, error => {
                this.isBusy = false;
            });
        } else {
            if (this.selectedRole && this.selectedRole.handle) {
                this.applyRole();
            }
        }
    }

    selectedTreeItemChanged(node: GenericTreeNode): void {
        if (node && node.data) {
            this.selectedRole = new Role(node.data);
            this.templateName = node.data.name;
            this.handle = node.data.handle;
        }
        this.initializeGridColumns();
        this.isTLSExpress = this.commonService.tlsExpressSystem;
        if (this.isTLSExpress) {
            this.getOpenInTitle();
        }
        this.loadData();
    }

    getOpenInTitle(): void {
        this.restService.post(Endpoint.orgManager_getReefExpressSettings, { handle: this.commonService.currentOrganizationHandle }).then(response => {
            if (response) {
                this.openInTitle = response.autoProjectCreation === AutoProjectCreation.Digital_Reef_and_Reef_Review ? 'Open in Reef Review' : 'Open in Relativity';
                this.exportToTitle = response.autoProjectCreation === AutoProjectCreation.Digital_Reef_and_Reef_Review ? 'Export to Reef Review' : 'Export to Relativity';
            }
        }, error => {
        });
    }

    initializeGridColumns(): void {
        this.panelGridColDefs = this.getPanelColumnDefs();
        this.tlsObjectGridColDefs = this.getTLSObjectColumnDefs();
        this.navigationTreeColDefs = this.getNavigationTreeColumnDefs();
        this.settingGridColDefs = this.getSettingsColumnDefs();
    }

    initializeGrids(viewData: RolePermissionViewData) {
        if (viewData && viewData.groupsToPermissionsMap) {
            const groups = Array.from(viewData.groupsToPermissionsMap.keys());
            const tlsObjectTypes = [ObjectActionListType.TLS_PROJECT, ObjectActionListType.TLS_DATA_SET, ObjectActionListType.TLS_USERS, ObjectActionListType.TLS_PROJECT_CONFIG, ObjectActionListType.TLS_PM_EMAIL_RECIPIENTS, ObjectActionListType.TLS_SALES_EMAIL_RECIPIENTS, ObjectActionListType.REEF_EXPRESS_PREFERENCES];
            let objectPermissionDetails = [];
            _.each(groups, (secureObjectGroupType) => {
                switch (secureObjectGroupType) {
                    case SecureObjectGroupType.DOCUMENT_ACTIONS:
                        {
                            this.initializeDocumentActions(this.viewData);
                        }
                        break;
                    case SecureObjectGroupType.NAVIGATION_TREE:
                        {
                            if (this.navigationTreeNodeGrid) {
                                this.navigationTreeNodeGrid.setColumnDefs(this.navigationTreeColDefs);
                                const permissionDetails = this.viewData.getPermissionDetailsByGroup(secureObjectGroupType);
                                this.navigationTreeNodeGrid.setData(permissionDetails, true);
                                this.navigationTreeNodeGrid.refreshColumns();
                            }
                        }
                        break;
                    case SecureObjectGroupType.OTHER:
                    case SecureObjectGroupType.COMMON_OTHER:
                        {
                            if (secureObjectGroupType === SecureObjectGroupType.OTHER) {
                                const tlsObjectPermissionDetails = this.viewData.getPermissionDetailsByGroup(secureObjectGroupType).filter(obj => tlsObjectTypes.includes(obj.secureObjectType));
                                if (this.tlsObjectGrid) {
                                    this.tlsObjectGrid.setColumnDefs(this.tlsObjectGridColDefs);
                                    this.tlsObjectGrid.setData(tlsObjectPermissionDetails, true);
                                    this.tlsObjectGrid.refreshColumns();
                                }
                            }
                            this.initializeOther(this.viewData);
                        }
                        break;
                    case SecureObjectGroupType.PANEL:
                        {
                            objectPermissionDetails = objectPermissionDetails.concat(this.viewData.getPermissionDetailsByGroup(secureObjectGroupType));
                        }
                        break;
                    case SecureObjectGroupType.PROJECT:
                        {
                            objectPermissionDetails = objectPermissionDetails.concat(this.viewData.getPermissionDetailsByGroup(secureObjectGroupType));//this.viewData.getPermissionDetailsByGroup(secureObjectGroupType).concat(objectPermissionDetails);
                        }
                        break;
                    case SecureObjectGroupType.SETTINGS:
                        {
                            if (this.settingGrid) {
                                this.settingGrid.setColumnDefs(this.settingGridColDefs);
                                const permissionDetails = this.viewData.getPermissionDetailsByGroup(secureObjectGroupType);
                                this.settingGrid.setData(permissionDetails, true);
                                this.settingGrid.refreshColumns();
                            }
                        }
                        break;
                    default:
                        break;
                }
            });

            if (this.panelGrid?.gridApi && objectPermissionDetails?.length > 0) {
                this.panelGrid.setColumnDefs(this.panelGridColDefs);
                this.panelGrid.setData(objectPermissionDetails, true);
                this.panelGrid.refreshColumns();
                this.panelGrid.gridOptions.suppressScrollOnNewData = true;
            }
        }
        // window.dispatchEvent(new Event('resize'));
    }

    cellClickedCallback = (rowNode: RowNode, action: CellActionType, event?: CellClickedEvent) => {
        if (rowNode && rowNode.data && action && event) {
            if (action === CellActionType.checkbox) {
                if (this.viewData && rowNode.data.secureObjectType && event.colDef.colId && event.colDef.field) {
                    let level = SecureObjectPermissionLevel.PROJECT;
                    if (event.colDef.field.includes('organization')) {
                        level = SecureObjectPermissionLevel.ORGANIZATION;
                    }

                    const currentDetails = this.viewData.rolePermissionDetailViewDataMap.get(rowNode.data.secureObjectType);
                    const value = rowNode.data[event.colDef.field];
                    if (isBoolean(value)) {
                        const prevDirty = this.viewData.isDirty(rowNode.data.secureObjectType, level);
                        const updatedPermissionDetails = this.viewData.updatePermissionDetail(rowNode.data.secureObjectType, currentDetails, event.colDef.colId, level, !value);
                        const nowDirty = this.viewData.isDirty(rowNode.data.secureObjectType, level);
                        rowNode.setData(updatedPermissionDetails);
                        if (prevDirty && !nowDirty) {
                            this.dirtyCount--;
                        } else if (!prevDirty && nowDirty) {
                            this.dirtyCount++;
                        }
                        this.isDirty = this.dirtyCount > 0;
                        this.validChanges = !this.isDirty;
                    }
                }
            }
        }
    }

    toggleCheckbox(objectActionListType: string): void {
        let type = objectActionListType;
        let value = false;
        let actionType = ActionType.CREATE;

        switch (objectActionListType) {
            case ObjectActionListType.DOCUMENT_TAGS:
                {
                    value = this.addRemoveTagsCheckbox;
                }
                break;
            case ObjectActionListType.DOCUMENT_FOLDER:
                {
                    value = this.addRemoveFolderCheckbox;
                }
                break;
            case ObjectActionListType.DOCUMENT_CUSTODIAN:
                {
                    value = this.addRemoveCustodianCheckbox;
                }
                break;
            case ObjectActionListType.DOWNLOAD_NATIVES:
                {
                    value = this.downloadNativeCheckbox;
                    actionType = ActionType.VIEW;
                }
                break;
            case ObjectActionListType.DOWNLOAD_PDF:
                {
                    value = this.downloadPDFCheckbox;
                    actionType = ActionType.VIEW;
                }
                break;
            case ObjectActionListType.AUDIT_LOG:
                {
                    value = this.auditAccessCheckbox;
                    actionType = ActionType.VIEW;
                }
                break;
            case ObjectActionListType.DOCUMENT_REPORTS:
                {
                    value = this.documentReportsCheckbox;
                    actionType = ActionType.VIEW;
                }
                break;
            case ObjectActionListType.LOCK_UNLOCK_WORKFLOW:
                {
                    value = this.workflowLockUnlockCheckbox;
                }
                break;
            case ObjectActionListType.CONNECTOR_ACCESS:
                {
                    value = this.connectorAccessCheckbox;
                    actionType = ActionType.VIEW;
                }
                break;
            case ObjectActionListType.PROJECT_MONITORING_ACCESS:
                {
                    value = this.projectMonitoringAccessCheckbox;
                    actionType = ActionType.VIEW;
                }
                break;
            case ObjectActionListType.TLS_OPEN_IN_RELATIVITY:
                {
                    value = this.openInRelativityCheckbox;
                    actionType = ActionType.VIEW;
                }
                break;
            case ObjectActionListType.TLS_BILLING_OVERVIEW_TAB:
                {
                    value = this.billingOverviewCheckbox;
                    actionType = ActionType.VIEW;
                }
                break;
            case ObjectActionListType.TLS_DR_ACCESS:
                {
                    value = this.drAccessCheckbox;
                    actionType = ActionType.VIEW;
                }
                break;
            case ObjectActionListType.TLS_VIEW_OTHERS_DATA_SETS:
                {
                    value = this.viewOthersDataSetsCheckbox;
                    actionType = ActionType.VIEW;
                }
                break;
            case ObjectActionListType.TLS_EXPORT_TO_VIEW:
                {
                    value = this.exportToRelativityCheckbox;
                    actionType = ActionType.VIEW;
                }
                break;
            default:
                {
                    type = null;
                    console.log('Error - toggleCheckbox() : Invalid ObjectTypeListType <' + objectActionListType + '>.');
                }
                break;
        }

        if (type !== null) {
            const currentDetails = this.viewData.rolePermissionDetailViewDataMap.get(type as any);
            const prevDirty = this.viewData.isDirty(type as any, SecureObjectPermissionLevel.PROJECT);
            this.viewData.updatePermissionDetail(type as any, currentDetails, actionType, SecureObjectPermissionLevel.PROJECT, value);
            const nowDirty = this.viewData.isDirty(type as any, SecureObjectPermissionLevel.PROJECT);
            if (prevDirty && !nowDirty) {
                this.dirtyCount--;
            } else if (!prevDirty && nowDirty) {
                this.dirtyCount++;
            }
            this.isDirty = this.dirtyCount > 0;
        }
    }

    isFieldDisabled(): boolean {
        let isDisabled = false;

        if ((this.selectedRole && this.selectedRole.isAdministratorRole(Role.organizationAdministrator)) || (!isNullOrUndefined(this.hasPermissions) && (this.hasPermissions === false))) {
            isDisabled = true;
        }
        return isDisabled;
    }

    initializeDocumentActions(viewData: RolePermissionViewData): void {

        let permissionDetails = viewData.getPermissionDetail(ObjectActionListType.DOCUMENT_TAGS);
        if ((permissionDetails !== null) && (!isUndefined(permissionDetails))) {
            this.addRemoveTagsCheckbox = permissionDetails.projectCreateState;
        }

        permissionDetails = viewData.getPermissionDetail(ObjectActionListType.DOCUMENT_FOLDER);
        if ((permissionDetails !== null) && (!isUndefined(permissionDetails))) {
            this.addRemoveFolderCheckbox = permissionDetails.projectCreateState;
        }

        permissionDetails = viewData.getPermissionDetail(ObjectActionListType.DOCUMENT_CUSTODIAN);
        if ((permissionDetails !== null) && (!isUndefined(permissionDetails))) {
            this.addRemoveCustodianCheckbox = permissionDetails.projectCreateState;
        }

        permissionDetails = viewData.getPermissionDetail(ObjectActionListType.DOWNLOAD_NATIVES);
        if ((permissionDetails !== null) && (!isUndefined(permissionDetails))) {
            this.downloadNativeCheckbox = permissionDetails.projectViewState;
        }

        permissionDetails = viewData.getPermissionDetail(ObjectActionListType.DOWNLOAD_PDF);
        if ((permissionDetails !== null) && (!isUndefined(permissionDetails))) {
            this.downloadPDFCheckbox = permissionDetails.projectViewState;
        }
    }

    initializeOther(viewData: RolePermissionViewData): void {
        let permissionDetails = viewData.getPermissionDetail(ObjectActionListType.CONNECTOR_ACCESS);
        if ((permissionDetails !== null) && (!isUndefined(permissionDetails))) {
            this.connectorAccessCheckbox = permissionDetails.projectViewState;
        }

        permissionDetails = viewData.getPermissionDetail(ObjectActionListType.PROJECT_MONITORING_ACCESS);
        if ((permissionDetails !== null) && (!isUndefined(permissionDetails))) {
            this.projectMonitoringAccessCheckbox = permissionDetails.projectViewState;
        }

        permissionDetails = viewData.getPermissionDetail(ObjectActionListType.AUDIT_LOG);
        if ((permissionDetails !== null) && (!isUndefined(permissionDetails))) {
            this.auditAccessCheckbox = permissionDetails.projectViewState;
        }

        permissionDetails = viewData.getPermissionDetail(ObjectActionListType.DOCUMENT_REPORTS);
        if ((permissionDetails !== null) && (!isUndefined(permissionDetails))) {
            this.documentReportsCheckbox = permissionDetails.projectViewState;
        }

        permissionDetails = viewData.getPermissionDetail(ObjectActionListType.LOCK_UNLOCK_WORKFLOW);
        if ((permissionDetails !== null) && (!isUndefined(permissionDetails))) {
            this.workflowLockUnlockCheckbox = permissionDetails.projectCreateState;
        }

        permissionDetails = viewData.getPermissionDetail(ObjectActionListType.TLS_OPEN_IN_RELATIVITY);
        if ((permissionDetails !== null) && (!isUndefined(permissionDetails))) {
            this.openInRelativityCheckbox = permissionDetails.projectViewState;
        }
        permissionDetails = viewData.getPermissionDetail(ObjectActionListType.TLS_BILLING_OVERVIEW_TAB);
        if ((permissionDetails !== null) && (!isUndefined(permissionDetails))) {
            this.billingOverviewCheckbox = permissionDetails.projectViewState;
        }
        permissionDetails = viewData.getPermissionDetail(ObjectActionListType.TLS_DR_ACCESS);
        if ((permissionDetails !== null) && (!isUndefined(permissionDetails))) {
            this.drAccessCheckbox = permissionDetails.projectViewState;
        }
        permissionDetails = viewData.getPermissionDetail(ObjectActionListType.TLS_VIEW_OTHERS_DATA_SETS);
        if ((permissionDetails !== null) && (!isUndefined(permissionDetails))) {
            this.viewOthersDataSetsCheckbox = permissionDetails.projectViewState;
        }
        permissionDetails = viewData.getPermissionDetail(ObjectActionListType.TLS_EXPORT_TO_VIEW);
        if ((permissionDetails !== null) && (!isUndefined(permissionDetails))) {
            this.exportToRelativityCheckbox = permissionDetails.projectViewState;
        }
    }

    setOriginalActionList(objectActionList: ObjectActionSet[]): void {
        this.roleOriginalObjectActionList = new Map<string, ObjectActionSet>();
        _.each(objectActionList, (anObjectActionList) => {
            const data = new ObjectActionSet(anObjectActionList);
            this.roleOriginalObjectActionList.set(data.type, data);
        });
    }

    saveChanges() {
        this.isBusyMessage = 'Saving Role Permissions ...';
        this.isBusy = true;
        const finalRolePermissions = this.viewData.getObjectActionList(this.roleOriginalObjectActionList);

        if (!isUndefined(finalRolePermissions) && (finalRolePermissions !== null)) {
            this.isDirty = false;
            this.validChanges = !this.isDirty;
            this.restService.post(Endpoint.orgManager_updateRole, {
                objectActionList: finalRolePermissions,
                name: this.templateName,
                handle: this.handle
            }).then(response => {
                if (response && (response.status === 'SUCCESS')) {
                    this.selectedRole.objectActionList = response.role.objectActionList;
                    this.viewData.resetState();
                    this.setOriginalActionList(response.role.objectActionList);
                }
                this.isBusy = false;
                this.isDirty = false;
                this.validChanges = !this.isDirty;
            }, () => {
                this.isBusy = false;
                this.isDirty = true;
                this.validChanges = !this.isDirty;
            });
        }
    }

    discardChanges() {
        // clear all viewData related structures and load again.
        this.viewData = null;
        this.dirtyCount = 0;
        this.loadData();
    }

    createRoleError(roleName: string): void {
        this.commonService.openErrorModal('Create role "' + roleName + '" failed.');
    }

    getRole(roleHandle: string): Role {
        let retrievedRole = null;
        if (this.handle) {
            this.restService.post(Endpoint.permissionManager_getCombinedUserRole, { entityHandle: roleHandle }).then(roleResponse => {
                if (roleResponse && roleResponse.status === 'SUCCESS') {
                    retrievedRole = roleResponse.getRole();
                }
            }, error => {
                this.isBusy = false;
            });
        }

        let finalRole = null;
        if (retrievedRole !== null) {
            finalRole = new Role(retrievedRole);
        }
        return finalRole;
    }

    applyRole(): void {
        this.restService.post(Endpoint.orgManager_getRole, { handle: this.selectedRole.handle }).then(roleResponse => {
            if (roleResponse && (roleResponse.status === 'SUCCESS')) {
                const currentRole = roleResponse.role;
                if ((currentRole !== null) && currentRole.objectActionList) {
                    this.selectedRole.objectActionList = currentRole.objectActionList;
                    this.setOriginalActionList(this.selectedRole.objectActionList);

                    if (this.roleOriginalObjectActionList && (this.roleOriginalObjectActionList.size > 0)) {
                        // update permission details for the selected role.
                        const objectActionListValues = Array.from(this.roleOriginalObjectActionList.values());
                        this.viewData.applyRoleToPermissionDetails(objectActionListValues, this.selectedRole);
                        if (this.viewData) {
                            this.initializeGrids(this.viewData);
                            this.isBusyMessage = '';
                            this.isBusy = false;
                        }
                    }
                } else {
                    console.log('Error: unable to get role with handle <' + this.selectedRole.handle + '>');
                }
            }
        }, error => {
            this.isBusyMessage = '';
            this.isBusy = false;
        });
    }

    getPanelColumnDefs(): Array<GenericColDef> {
        const panelGridColDefs = new Array<GenericColDef>(
            {
                headerName: 'Object',
                field: 'displayValue',
                colId: 'displayValue',
                automationId: this.panelAutomationId,
                cellTemplateType: CellTemplateType.nameIconLabel,
                width: 463,
                minWidth: 100,
                lockVisible: true
            },
            {
                headerName: 'View',
                field: 'projectViewState',
                colId: 'VIEW',
                automationId: this.panelAutomationId,
                cellTemplateType: this.isFieldDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                width: 75,
                minWidth: 75,
                suppressSizeToFit: true,
                headerAlignDirection: AlignDirection.center,
                lockVisible: true
            },
            {
                headerName: 'Add/Edit',
                field: 'projectCreateState',
                colId: 'CREATE',
                cellTemplateType: this.isFieldDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                automationId: this.panelAutomationId,
                width: 75,
                minWidth: 75,
                suppressSizeToFit: true,
                headerAlignDirection: AlignDirection.center,
                lockVisible: true
            },
            {
                headerName: 'Delete',
                field: 'projectDeleteState',
                colId: 'DELETE',
                cellTemplateType: this.isFieldDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                width: 75,
                minWidth: 75,
                suppressSizeToFit: true,
                headerAlignDirection: AlignDirection.center,
                automationId: this.panelAutomationId,
                lockVisible: true
            }
        );
        return panelGridColDefs;
    }

    getNavigationTreeColumnDefs(): Array<GenericColDef> {
        const navigationTreeColDefs = new Array<GenericColDef>(
            {
                headerName: 'Navigation Tree Node',
                field: 'displayValue',
                colId: 'displayValue',
                automationId: this.panelAutomationId,
                cellTemplateType: CellTemplateType.nameIconLabel,
                width: 463,
                minWidth: 100,
                lockVisible: true
            },
            {
                headerName: 'View',
                field: 'projectViewState',
                colId: 'VIEW',
                automationId: this.panelAutomationId,
                cellTemplateType: this.isFieldDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                width: 75,
                minWidth: 75,
                suppressSizeToFit: true,
                headerAlignDirection: AlignDirection.center,
                lockVisible: true
            },
            {
                headerName: 'Add/Edit',
                field: 'projectCreateState',
                colId: 'CREATE',
                cellTemplateType: this.isFieldDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                automationId: this.panelAutomationId,
                width: 75,
                minWidth: 75,
                suppressSizeToFit: true,
                headerAlignDirection: AlignDirection.center,
                lockVisible: true
            },
            {
                headerName: 'Delete',
                field: 'projectDeleteState',
                colId: 'DELETE',
                cellTemplateType: this.isFieldDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                width: 75,
                minWidth: 75,
                suppressSizeToFit: true,
                headerAlignDirection: AlignDirection.center,
                automationId: this.panelAutomationId,
                lockVisible: true
            }
        );
        return navigationTreeColDefs;
    }


    getSettingsColumnDefs(): Array<any> {
        const settingGridColDefs = new Array<any>(
            {
                headerName: 'Setting',
                field: 'displayValue',
                colId: 'displayValue',
                automationId: this.panelAutomationId,
                cellTemplateType: CellTemplateType.nameIconLabel,
                minWidth: 100,
                lockVisible: true
            },
            {
                headerName: 'Project-level',
                groupId: 'projectLevelHeader',
                children: [
                    {
                        headerName: 'View',
                        field: 'projectViewState',
                        colId: 'VIEW',
                        automationId: this.panelAutomationId,
                        cellTemplateType: this.isFieldDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                        width: 75,
                        minWidth: 75,
                        suppressSizeToFit: true,
                        headerAlignDirection: AlignDirection.center,
                        lockVisible: true,
                        cellRenderer: 'gridCellRenderer'
                    },
                    {
                        headerName: 'Add/Edit',
                        field: 'projectCreateState',
                        colId: 'CREATE',
                        cellTemplateType: this.isFieldDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                        automationId: this.panelAutomationId,
                        width: 75,
                        minWidth: 75,
                        suppressSizeToFit: true,
                        headerAlignDirection: AlignDirection.center,
                        lockVisible: true,
                        cellRenderer: 'gridCellRenderer'
                    },
                    {
                        headerName: 'Delete',
                        field: 'projectDeleteState',
                        colId: 'DELETE',
                        cellTemplateType: this.isFieldDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                        width: 75,
                        minWidth: 75,
                        suppressSizeToFit: true,
                        headerAlignDirection: AlignDirection.center,
                        automationId: this.panelAutomationId,
                        lockVisible: true,
                        cellRenderer: 'gridCellRenderer'
                    }
                ]
            },
            {
                headerName: 'Organization-level',
                groupId: 'organizationLevelHeader',
                children: [
                    {
                        headerName: 'View',
                        field: 'organizationViewState',
                        colId: 'VIEW',
                        automationId: this.panelAutomationId,
                        cellTemplateType: this.isFieldDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                        width: 75,
                        minWidth: 75,
                        suppressSizeToFit: true,
                        headerAlignDirection: AlignDirection.center,
                        lockVisible: true,
                        cellRenderer: 'gridCellRenderer'
                    },
                    {
                        headerName: 'Add/Edit',
                        field: 'organizationCreateState',
                        colId: 'CREATE',
                        cellTemplateType: this.isFieldDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                        automationId: this.panelAutomationId,
                        width: 75,
                        minWidth: 75,
                        suppressSizeToFit: true,
                        headerAlignDirection: AlignDirection.center,
                        lockVisible: true,
                        cellRenderer: 'gridCellRenderer'
                    },
                    {
                        headerName: 'Delete',
                        field: 'organizationDeleteState',
                        colId: 'DELETE',
                        cellTemplateType: this.isFieldDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                        width: 75,
                        minWidth: 75,
                        suppressSizeToFit: true,
                        headerAlignDirection: AlignDirection.center,
                        automationId: this.panelAutomationId,
                        lockVisible: true,
                        cellRenderer: 'gridCellRenderer'
                    }
                ]
            }
        );
        return settingGridColDefs;
    }

    getTLSObjectColumnDefs(): Array<GenericColDef> {
        const tlsObjectGridColDefs = new Array<GenericColDef>(
            {
                headerName: 'Reef Express Object',
                field: 'displayValue',
                colId: 'displayValue',
                automationId: this.tlsObjectAutomationId,
                cellTemplateType: CellTemplateType.nameIconLabel,
                minWidth: 100,
                lockVisible: true
            },
            {
                headerName: 'View',
                field: 'projectViewState',
                colId: 'VIEW',
                automationId: this.panelAutomationId,
                cellTemplateType: this.isFieldDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                width: 75,
                minWidth: 75,
                suppressSizeToFit: true,
                headerAlignDirection: AlignDirection.center,
                lockVisible: true
            },
            {
                headerName: 'Add/Edit',
                field: 'projectCreateState',
                colId: 'CREATE',
                cellTemplateType: this.isFieldDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                automationId: this.panelAutomationId,
                width: 75,
                minWidth: 75,
                suppressSizeToFit: true,
                headerAlignDirection: AlignDirection.center,
                lockVisible: true
            },
            {
                headerName: 'Delete',
                field: 'projectDeleteState',
                colId: 'DELETE',
                cellTemplateType: this.isFieldDisabled() ? CellTemplateType.disabledCheckbox : CellTemplateType.checkbox,
                width: 75,
                minWidth: 75,
                suppressSizeToFit: true,
                headerAlignDirection: AlignDirection.center,
                automationId: this.panelAutomationId,
                lockVisible: true
            }
        );
        return tlsObjectGridColDefs;
    }
}
